import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brain } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const MAGIC_REASONS = {
  expired: "That one-tap login link has expired — sign in below and we'll take you where you were going.",
  used: "That one-tap link has already been used — sign in below to continue.",
  invalid: "That login link isn't valid anymore — sign in below to keep going.",
  success: "✓ Signed in via email link",
};

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/app";
  const magic = params.get("magic");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (magic && MAGIC_REASONS[magic]) {
      toast.info(MAGIC_REASONS[magic]);
    }
  }, [magic]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav(next);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-border/60 relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-20 pointer-events-none" />
        <Link to="/" className="relative flex items-center gap-2 font-display font-semibold">
          <Brain className="w-5 h-5" /> RAGNES
        </Link>
        <div className="relative">
          <p className="eyebrow mb-4">A living knowledge base</p>
          <p className="font-display text-3xl leading-tight max-w-md">
            "The single source of truth for how your company actually works."
          </p>
        </div>
        <p className="relative text-xs text-muted-foreground font-mono">workspace.access</p>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <p className="eyebrow mb-3">Sign in</p>
            <h1 className="font-display text-3xl">Access your workspace</h1>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
            </div>
            {err && <p className="text-sm text-destructive" data-testid="login-error">{err}</p>}
            <Button type="submit" disabled={busy} data-testid="login-submit-btn" className="w-full rounded-full h-11">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            No account?{" "}
            <Link to="/register" className="text-foreground underline underline-offset-4" data-testid="login-register-link">Create workspace</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
